import {useAtomValue} from 'jotai';
import {isAxiosError} from 'axios';
import {useAuth} from '@api/auth';
import {IS_MOCK} from '@api/mock-mode';
import {roleAtom} from '@lib/atoms';
import {useActiveWorkspace, useMyAccess, useMyWorkspaces} from './hooks';
import type {MyAccess} from './types';

/** Явное состояние доступа: ошибка/ожидание никогда не означают общий scope. */
export type AccessStatus = 'pending' | 'ready' | 'error' | 'denied';

/** Проверяет принадлежность ответа текущему actor/workspace и активность членства. */
export const workspaceAccessStatus = (
  actorSid: string | null,
  workspaceSid: string | null,
  access: MyAccess | undefined,
  pending: boolean,
  error: unknown,
): AccessStatus => {
  if (error) {
    const status = isAxiosError(error) ? error.response?.status : undefined;
    return status === 403 || status === 404 ? 'denied' : 'error';
  }
  if (pending) return 'pending';
  if (!actorSid || !workspaceSid) return 'denied';
  if (!access) return 'error';
  if (access.userSid !== actorSid || access.workspaceSid !== workspaceSid)
    return 'denied';
  return access.isOperator || access.membershipState === 'active'
    ? 'ready'
    : 'denied';
};

/** Единый контекст ЛК, без roster, эвристик grants и DEV-подмены реальной роли. */
export const usePersonalScope = () => {
  const auth = useAuth();
  const workspaces = useMyWorkspaces();
  const {activeId} = useActiveWorkspace();
  const query = useMyAccess(activeId);
  const demoRole = useAtomValue(roleAtom);
  const actorSid = IS_MOCK
    ? demoRole === 'manager'
      ? 'emp-1'
      : 'mock-user-sid'
    : (auth.currentUser?.sid ?? null);
  const workspaceSid = IS_MOCK ? 'mock-workspace' : activeId;
  const status = IS_MOCK
    ? 'ready'
    : workspaceAccessStatus(
        actorSid,
        workspaceSid,
        query.data,
        auth.isLoading ||
          workspaces.isPending ||
          Boolean(activeId && query.isPending),
        auth.error || workspaces.error || query.error,
      );
  const role = IS_MOCK
    ? demoRole === 'manager'
      ? 'manager'
      : 'sales-head'
    : (query.data?.role ?? null);
  const isOperator = !IS_MOCK && Boolean(query.data?.isOperator);
  const isPersonal = role === 'manager' && !isOperator;
  const isReady = status === 'ready';
  const signature = JSON.stringify([
    actorSid,
    workspaceSid,
    status,
    role,
    isOperator,
    query.data?.membershipState,
    query.data?.grants ?? [],
  ]);
  return {
    actorSid,
    workspaceSid,
    role,
    isOperator,
    isPersonal,
    isReady,
    status,
    userSid: isReady ? actorSid : null,
    access: isReady ? query.data : undefined,
    signature,
    queryScope: [
      actorSid,
      workspaceSid,
      isPersonal ? 'self' : 'action',
      signature,
    ] as const,
    retry: () => {
      void workspaces.refetch();
      if (activeId) void query.refetch();
    },
  };
};
